import {MapPin, Ticket, User, AlertCircle, Clock, TrendingUp, Zap, Shield, ChevronDown, ChevronUp, Home } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useMarkerStore, useRouteStore } from "@/store/locationStore";
import { apiGetTicketsByEnginnerName, apiRoutes } from "@/services/user.api";
import { Button } from "../ui/button";
import { PATHS } from "@/constants/page-paths";
import { useNavigate } from "react-router-dom";

interface SidebarItemProps {
  icon?: ReactNode;
  active?: boolean;
  alert?: boolean;
  text?: string;
}

interface Ticket {
  id: string;
  customerName: string;
  address: string;
  ticketType: string;
  urgency: 'Low' | 'Medium' | 'High' | 'Critical';
}

const URGENCY_COLORS: Record<string, string> = {
  low: "bg-slate-100 text-slate-700 border-slate-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  critical: "bg-red-100 text-red-700 border-red-200",
};

const URGENCY_ICONS: Record<string, ReactNode> = {
  low: <Clock className="h-3 w-3" />,
  medium: <AlertCircle className="h-3 w-3" />,
  high: <Zap className="h-3 w-3" />,
  critical: <Shield className="h-3 w-3" />,
};

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-16">
    <div className="relative">
      <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
      <div className="absolute inset-0 flex items-center justify-center">
        <MapPin className="h-6 w-6 text-blue-600" />
      </div>
    </div>
    <p className="text-base text-gray-500 mt-6 font-medium">Loading technician data...</p>
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="flex flex-col items-center justify-center py-16">
    <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl mb-6">
      <Ticket className="w-10 h-10 text-blue-600" />
    </div>
    <p className="text-base text-gray-500 text-center font-medium">{message}</p>
    <p className="text-sm text-gray-400 text-center mt-2">Assign complaints to see them here</p>
  </div>
);

export function SidebarItem({
  icon,
  active = false,
  alert = false,
}: SidebarItemProps) {
  return (
    <li
      className={`relative flex items-center py-3 px-4 my-2 font-medium rounded-lg cursor-pointer transition-all duration-200 group ${
        active
          ? "bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border-r-2 border-blue-600 shadow-sm"
          : "hover:bg-gray-50 text-gray-700 hover:shadow-sm"
      }`}
    >
      {icon}
      <span className="overflow-hidden transition-all"></span>
      {alert && (
        <div className="absolute right-3 w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
      )}

      <div
        className={`absolute left-full rounded-lg px-4 py-2 ml-4 bg-gray-900 text-white text-sm invisible opacity-0 -translate-x-2 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 z-50 shadow-lg`}
      ></div>
    </li>
  );
}

const LocationSidebar = () => {
  const [selectedEngineer, setSelectedEngineer] = useState("Suman");
  const { setRoutePath } = useRouteStore();
  const [engineerTickets, setEngineerTickets] = useState<Ticket[]>([]);
  const [orderedTickets, setOrderedTickets] = useState<Ticket[]>([]);
  const [clicked, setClicked] = useState<boolean>();
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [isCalculatingRoute, setIsCalculatingRoute] = useState(false);
  const [isComplaintsExpanded, setIsComplaintsExpanded] = useState(true);
  const { setMarkerPoints } = useMarkerStore();


  useEffect(() => {
    async function fetchTickets() {
      setIsLoadingTickets(true);
      try {
        const data = await apiGetTicketsByEnginnerName(selectedEngineer);
        console.log(data);
        setEngineerTickets(data.tickets || []);
      } catch (error) {
        console.error("Error fetching tickets:", error);
      } finally {
        setIsLoadingTickets(false);
      }
    }
    fetchTickets();
  }, [selectedEngineer]);

  const handleClick = async () => {
    setIsCalculatingRoute(true);
    setClicked(true);

    async function sendRoutePayload() {
      const payload = {
        engineerName: selectedEngineer,
        date: "2025-07-30",
        currentLocation: {
          lat: 28.49357,
          lng: 77.09366,
        },
      };
      try {
        const response = await apiRoutes(payload);
        setOrderedTickets(response?.data?.orderedTickets || []);
        console.log("ordered tickets:", orderedTickets);
      } catch (error) {
        console.error("Error sending route payload:", error);
      } finally {
        setIsCalculatingRoute(false);
        console.log(isCalculatingRoute);
      }
    }
    await sendRoutePayload();

    async function fetchRoute() {
      const payload = {
        engineerName: selectedEngineer,
        date: "2025-07-30",
        currentLocation: { lat: 28.4935677, lng: 77.093661 },
      };
      const response = await apiRoutes(payload);
      const legs = response.data.optimizedRoute?.legs || [];
      const path: google.maps.LatLngLiteral[] = [];
      legs.forEach((leg: { end_location: google.maps.LatLngLiteral }) => {
        path.push(leg.end_location);
      });
      setRoutePath(path);
      console.log(path, setRoutePath);
      setMarkerPoints(path);
    }
    fetchRoute();
  };

  const getUrgencyLevel = (urgency: string) => {
    const level = urgency?.toLowerCase();
    return {
      color: URGENCY_COLORS[level] || 'bg-gray-100 text-gray-700 border-gray-200',
      icon: URGENCY_ICONS[level] || <Clock className="h-3 w-3" />
    };
  };

  const navigate = useNavigate();

  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-200 shadow-sm min-w-[400px] max-w-[500px]">
      {/* Premium Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8">
            <div className="flex items-center gap-2 mb-2">
            <button className="p-01 bg-white/20 rounded-xl cursor-pointer" onClick={() => navigate(PATHS.DASHBOARD)}>
              <Home className="h-4 w-4" />
            </button>
            <span className="text-sm">Dashboard</span>
            </div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div>
              <h2 className="text-xl font-bold">Technician Map</h2>
              <p className="text-blue-100 text-base">Route Optimization System</p>
            </div>
          </div>
        </div>
        
        {/* Engineer Selection */}
        <div className="space-y-3">
          <label className="block text-base font-medium text-blue-100">
            Select Technician
          </label>
          <Select value={selectedEngineer} onValueChange={(val) => setSelectedEngineer(val)}>
            <SelectTrigger className="w-full bg-white/10 border-white/20 text-white placeholder:text-blue-200 h-12 text-base">
              <SelectValue placeholder="Select a technician" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="Rohit">Rohit</SelectItem>
                <SelectItem value="Amit">Amit</SelectItem>
                <SelectItem value="Suman">Suman</SelectItem>
                <SelectItem value="Arif">Arif</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Technician Profile Card */}
      <div className="p-8 border-b border-gray-100">
        <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-blue-100">
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <User className="h-8 w-8 text-white" />
              </div>
              <div className="absolute -bottom-2 -right-2 h-5 w-5 bg-green-500 rounded-full border-3 border-white"></div>
            </div>
            <div className="flex-1">
              <div className="font-bold text-gray-900 text-lg">{selectedEngineer}</div>
              <div className="text-base text-gray-600">Senior Electrical Technician</div>
            </div>
            {/* <button className="p-3 rounded-xl bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors">
              <Phone className="h-5 w-5" />
            </button> */}
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      {/* <div className="px-8 py-6 border-b border-gray-100">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-700">Resolution Rate</span>
            </div>
            <div className="text-xl font-bold text-emerald-900">{performanceMetrics.resolutionRate}</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Avg Response</span>
              </div>
            </div>
            <div className="text-xl font-bold text-blue-900">{performanceMetrics.responseTime}</div>
          </div>
        </div>
      </div> */}

      {/* Assigned Tickets - Collapsible */}
      <div className="flex-1 overflow-y-auto">
        <div className='pt-4 px-8'>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-bold text-gray-900">Assigned Complaints</h3>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                {engineerTickets.length} active
              </span>
              <button
                onClick={() => setIsComplaintsExpanded(!isComplaintsExpanded)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {isComplaintsExpanded ? (
                  <ChevronUp className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Collapsible Content */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isComplaintsExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="space-y-4">
              {isLoadingTickets ? (
                <LoadingState />
              ) : engineerTickets.length > 0 ? (
                engineerTickets.map((ticket, idx) => {
                  const urgency = getUrgencyLevel(ticket.urgency);
                  return (
                    <div
                      key={ticket.id || idx}
                      className="group bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-lg hover:border-blue-200 transition-all duration-200 cursor-pointer"
                    >
                      <div className="flex items-start gap-5">
                        <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl group-hover:from-blue-200 group-hover:to-indigo-200 transition-colors">
                          <AlertCircle className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="font-semibold text-gray-900 text-base truncate">
                              {ticket.customerName}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${urgency.color} flex items-center gap-2`}>
                              {urgency.icon}
                              {ticket.urgency}
                            </span>
                          </div>
                          <div className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {ticket.address}
                          </div>
                          <div className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg inline-block">
                            {ticket.ticketType}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <EmptyState message="No complaints assigned to this technician" />
              )}
            </div>
          </div>
        </div>

        {/* Optimized Route Section */}
        {clicked && engineerTickets.length > 0 && (
          <div className="p-8 border-t border-gray-100 bg-gradient-to-br from-green-50 to-emerald-50">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-xl">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <h3 className="text-base font-bold text-gray-900">Optimized Route</h3>
              </div>
              <span className="text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full font-medium">
                AI Optimized
              </span>
            </div>
            <div className="space-y-4">
              {orderedTickets.length > 0 ? (
                orderedTickets.map((ticket, idx) => (
                  <div
                    key={ticket.id || idx}
                    className="bg-white border border-green-200 rounded-2xl p-5 shadow-sm"
                  >
                    <div className="flex items-start gap-5">
                      <div className="relative">
                        <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl">
                          <MapPin className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="absolute -top-2 -right-2 h-6 w-6 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="font-semibold text-gray-900 text-base truncate">
                            {ticket.customerName}
                          </span>
                          <span className="text-sm text-green-600 font-medium bg-green-100 px-3 py-1 rounded-full">
                            Stop #{idx + 1}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mb-2 flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          {ticket.address}
                        </div>
                        <div className="text-sm text-gray-600 bg-green-50 px-3 py-2 rounded-lg inline-block">
                          {ticket.ticketType}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="relative inline-block">
                    <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                  <p className="text-base text-gray-600 mt-6 font-medium">Optimizing route...</p>
                  <p className="text-sm text-gray-400 mt-2">AI is calculating the most efficient path</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Premium Action Button */}
      {engineerTickets.length > 0 && (
        <div className="p-8 border-t border-gray-100 bg-gradient-to-r from-slate-50 to-gray-50">
          <Button
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-4 rounded-2xl transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] text-base"
            onClick={handleClick}
            disabled={engineerTickets.length === 0 || isCalculatingRoute}
          >
            {isCalculatingRoute ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                Optimizing Route...
              </>
            ) : (
              <>
                <MapPin className="w-5 h-5 mr-3" />
                Generate Optimized Route
              </>
            )}
          </Button>
          <p className="text-sm text-gray-500 text-center mt-3">
            AI-powered route optimization for maximum efficiency
          </p>
        </div>
      )}
    </div>
  );
};

export default LocationSidebar;